import React, { useCallback, useEffect, useState } from "react";
import axios from "../../api";
import {
  Container,
  Paper,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  MenuItem,
  Pagination,
  CircularProgress,
  Chip,
  Button,
} from "@mui/material";
import { History, Refresh, Download } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AdminLayout from "../AdminLayout";

const PER_PAGE = 25;

function AuditLogPage({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [exporting, setExporting] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/audit", {
        params: { page, perPage: PER_PAGE, action, entityType },
      });
      setRows(res.data.result || []);
      setTotal(res.data.pageData?.total || 0);
    } catch (err) {
      toast.error("Failed to load audit log.");
    } finally {
      setLoading(false);
    }
  }, [page, action, entityType]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleLogout = () => {
    if (onLogout) return onLogout();
    navigate("/login");
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get("/audit/export", {
        params: { action, entityType },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit CSV downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const pageCount = Math.ceil(total / PER_PAGE) || 1;

  return (
    <AdminLayout
      title="Audit Log"
      subtitle="Who did what — assignments, imports, verification, reviews"
      onLogout={handleLogout}
    >
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <History color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Recent activity
            </Typography>
            <Chip size="small" label={total} />
            <Box sx={{ flexGrow: 1 }} />
            <TextField
              size="small"
              label="Action contains"
              value={action}
              onChange={(e) => {
                setPage(1);
                setAction(e.target.value);
              }}
              sx={{ minWidth: 180 }}
              placeholder="e.g. asset.assign"
            />
            <TextField
              size="small"
              select
              label="Entity"
              value={entityType}
              onChange={(e) => {
                setPage(1);
                setEntityType(e.target.value);
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Asset">Asset</MenuItem>
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="AssetUndertaking">Undertaking</MenuItem>
              <MenuItem value="Settings">Settings</MenuItem>
            </TextField>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={fetchLogs}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download />}
              onClick={exportCsv}
              disabled={exporting || loading}
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell scope="col">When</TableCell>
                      <TableCell scope="col">Action</TableCell>
                      <TableCell scope="col">Entity</TableCell>
                      <TableCell scope="col">Actor</TableCell>
                      <TableCell scope="col">Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r._id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {r.createdAt
                            ? new Date(r.createdAt).toLocaleString()
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={r.action || "—"} />
                        </TableCell>
                        <TableCell>
                          {r.entityType || "—"}
                          {r.entityId ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {String(r.entityId).slice(-8)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {r.actorEmail || r.actorName || r.userEmail || "—"}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "monospace",
                              wordBreak: "break-word",
                            }}
                          >
                            {r.details
                              ? JSON.stringify(r.details).slice(0, 160)
                              : "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          {action || entityType
                            ? "No audit entries match these filters."
                            : "No activity recorded yet."}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              {total > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={(e, v) => setPage(v)}
                  />
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
      <ToastContainer position="top-right" autoClose={2500} />
    </AdminLayout>
  );
}

export default AuditLogPage;
